import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Divider,
  Image,
  Loader,
  Select,
  TextInput
} from '@mantine/core';
import { Plus } from 'tabler-icons-react';
import { useNavigate } from 'react-router-dom';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import { Context as AuthContext } from '../../../providers/AuthContextProvider';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import { getEcomVendorProductStatus } from '../../../helpers/vendorHelper';
import { PRODUCT_CATEGORY_LIST } from '../../../config/productCategories';
import { ECOM_PRODUCT_STATUS_LIST } from '../../../config/constants';

const VendorProductAssignModal = ({
  title,
  isOpen,
  onClose,
  onConfirm,
  showCategoryFilter,
  showCollectionFilter,
  showDesignFilter,
  showProductStatusFilter,
  selectedProducts
}) => {
  const navigate = useNavigate();
  const { state: authState } = useContext(AuthContext);
  const {
    state,
    fetchVendorProductOptionsSummary,
    fetchVendorResourceOptions
  } = useContext(VendorContext);
  const [filterState, setFilterState] = useState({
    search: '',
    fkEcomProductStatus: null,
    fkEcomCategory: null,
    fkEcomVendorCollection: null,
    fkEcomVendorDesign: null
  });
  const [debouncedFilter, setDebouncedFilter] = useState(null);
  const [formState, setFormState] = useState({ selectedProducts: [] });

  const {
    loading: fetchingData,
    value: data,
    filter
  } = state.ecomVendorProductSummaryOptions;
  const searchedProducts = data?.ecomVendorProducts || [];

  const loading =
    fetchingData ||
    filter?.search !== debouncedFilter?.search ||
    filter?.fkEcomCategory !== debouncedFilter?.fkEcomCategory ||
    filter?.fkEcomProductStatus !== debouncedFilter?.fkEcomProductStatus ||
    filter?.fkEcomVendorCollection !==
      debouncedFilter?.fkEcomVendorCollection ||
    filter?.fkEcomVendorDesign !== debouncedFilter?.fkEcomVendorDesign;

  const hasFilteredProducts =
    (filterState.search && filterState.search.length >= 3) ||
    filterState.fkEcomProductStatus ||
    filterState.fkEcomCategory ||
    filterState.fkEcomVendorCollection ||
    filterState.fkEcomVendorDesign;

  const isAllSelected =
    hasFilteredProducts &&
    !loading &&
    searchedProducts.length > 0 &&
    searchedProducts.every((p) =>
      formState.selectedProducts.some((sp) => sp.uuid === p.uuid)
    );

  useEffect(() => {
    if (isOpen) {
      setDebouncedFilter(null);
      setFilterState({
        search: '',
        fkEcomProductStatus: null,
        fkEcomCategory: null,
        fkEcomVendorCollection: null,
        fkEcomVendorDesign: null
      });
      setFormState({
        selectedProducts:
          selectedProducts?.map((p) => ({
            uuid: p.uuid,
            name: p.name,
            previewImage: p.previewImage,
            fkEcomProductStatus: p.fkEcomProductStatus
          })) || []
      });

      if (
        (showCollectionFilter || showDesignFilter) &&
        (state.ecomVendorResourceOptions.value === null ||
          state.ecomVendorResourceOptions.filter?.pkEcomVendor !==
            authState.pkEcomVendor)
      ) {
        fetchVendorResourceOptions(authState.pkEcomVendor);
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (debouncedFilter !== null && hasFilteredProducts) {
      fetchVendorProductOptionsSummary(authState.pkEcomVendor, debouncedFilter);
    }
  }, [debouncedFilter]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedFilter(filterState);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [filterState]);

  return (
    <ResponsiveModal
      formSectionProps={{
        onCancel: onClose,
        onSubmit: () => {
          onConfirm(formState.selectedProducts);
        },
        padding: 0,
        submitTitle: 'Confirm'
      }}
      isOpen={isOpen}
      onClose={onClose}
      size={600}
      title={title || 'Edit products in collection'}
    >
      <AppStack
        style={{
          gap: 0,
          minHeight: 400,
          overflow: 'auto',
          flex: 1
        }}
      >
        <AppStack style={{ padding: 5, gap: 5 }}>
          <TextInput
            onChange={(e) =>
              setFilterState({
                ...filterState,
                search: e.currentTarget.value
              })
            }
            placeholder="Filter by title or description..."
            style={{ flex: 1 }}
            value={filterState.search || ''}
          />

          {showProductStatusFilter && (
            <Select
              clearable
              data={ECOM_PRODUCT_STATUS_LIST.map((s) => ({
                label: s.name,
                value: s.value.toString()
              }))}
              onChange={(v) =>
                setFilterState({
                  ...filterState,
                  fkEcomProductStatus: v
                })
              }
              placeholder="Filter by status..."
              searchable
              style={{ flex: 1 }}
              value={filterState.fkEcomProductStatus || ''}
            />
          )}

          {showCategoryFilter && (
            <Select
              clearable
              data={PRODUCT_CATEGORY_LIST.map((c) => ({
                label: c.name,
                value: c.value.toString()
              }))}
              onChange={(v) =>
                setFilterState({
                  ...filterState,
                  fkEcomCategory: v
                })
              }
              placeholder="Filter by category..."
              searchable
              style={{ flex: 1 }}
              value={filterState.fkEcomCategory || ''}
            />
          )}

          {showCollectionFilter && (
            <Select
              clearable
              data={
                state.ecomVendorResourceOptions.value?.ecomVendorCollections.map(
                  (c) => ({
                    label: c.name,
                    value: c.pkEcomVendorCollection.toString()
                  })
                ) || []
              }
              onChange={(v) =>
                setFilterState({
                  ...filterState,
                  fkEcomVendorCollection: v
                })
              }
              placeholder="Filter by collection..."
              rightSection={
                state.ecomVendorResourceOptions.loading ? (
                  <Loader color="dark" size={14} />
                ) : null
              }
              searchable
              style={{ flex: 1 }}
              value={filterState.fkEcomVendorCollection || ''}
            />
          )}

          {showDesignFilter && (
            <Select
              clearable
              data={
                state.ecomVendorResourceOptions.value?.ecomVendorDesigns.map(
                  (c) => ({
                    label: c.name,
                    value: c.pkEcomVendorDesign.toString()
                  })
                ) || []
              }
              onChange={(v) =>
                setFilterState({
                  ...filterState,
                  fkEcomVendorDesign: v
                })
              }
              placeholder="Filter by design..."
              rightSection={
                state.ecomVendorResourceOptions.loading ? (
                  <Loader color="dark" size={14} />
                ) : null
              }
              searchable
              style={{ flex: 1 }}
              value={filterState.fkEcomVendorDesign || ''}
            />
          )}
        </AppStack>
        <Divider />
        <AppFlexbox
          style={{
            gap: 5,
            padding: 8,
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f3f3f3'
          }}
        >
          <AppFlexbox style={{ gap: 5, alignItems: 'center' }}>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              Products
            </AppText>

            {hasFilteredProducts && !loading && (
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                ({searchedProducts.length})
              </AppText>
            )}
          </AppFlexbox>

          <Checkbox
            checked={isAllSelected || false}
            disabled={!hasFilteredProducts}
            label="Select all"
            onChange={() => {
              if (isAllSelected) {
                setFormState({
                  ...formState,
                  selectedProducts: formState.selectedProducts.filter((sp) =>
                    searchedProducts.every((p) => p.uuid !== sp.uuid)
                  )
                });
              }
              else {
                const newSelectedProducts = [
                  ...formState.selectedProducts,
                  ...searchedProducts.filter((p) =>
                    formState.selectedProducts.every((sp) => sp.uuid !== p.uuid)
                  )
                ];
                setFormState({
                  ...formState,
                  selectedProducts: newSelectedProducts
                });
              }
            }}
            styles={{
              label: { fontWeight: 500, cursor: 'pointer' },
              input: { cursor: 'pointer' }
            }}
          />
        </AppFlexbox>
        <Divider />
        <AppStack
          style={{
            gap: 0,
            flex: 1
          }}
        >
          {!hasFilteredProducts ? (
            <AppStack
              style={{
                gap: 20,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 10px'
              }}
            >
              <AppStack
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#666',
                  textAlign: 'center'
                }}
              >
                Use the filters above to find products to add.
              </AppStack>

              {debouncedFilter?.search && debouncedFilter.search.length < 3 && (
                <AppStack
                  style={{
                    fontSize: 12,
                    color: '#666',
                    fontStyle: 'italic',
                    textAlign: 'center'
                  }}
                >
                  Please enter at least 3 characters to search by title.
                </AppStack>
              )}
            </AppStack>
          ) : loading ? (
            <AppStack
              style={{
                gap: 5,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 10px',
                flex: 1
              }}
            >
              <Loader color="dark" size={34} />
            </AppStack>
          ) : searchedProducts.length === 0 ? (
            <AppStack
              style={{
                gap: 5,
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px 10px'
              }}
            >
              <AppStack style={{ padding: 16, fontWeight: 500 }}>
                No products found.
              </AppStack>
              <Button
                color="dark"
                leftSection={<Plus size={18} />}
                onClick={() => {
                  navigate('/vendor/products/new');
                }}
                type="button"
                variant="outline"
              >
                Add a new product
              </Button>
            </AppStack>
          ) : (
            searchedProducts
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((p) => {
                const productStatusInfo = getEcomVendorProductStatus(p);
                const selected = formState.selectedProducts.some(
                  (sp) => sp.uuid === p.uuid
                );

                return (
                  <AppFlexbox
                    key={p.uuid}
                    onClick={() => {
                      if (selected) {
                        setFormState({
                          ...formState,
                          selectedProducts: formState.selectedProducts.filter(
                            (sp) => sp.uuid !== p.uuid
                          )
                        });
                      }
                      else {
                        setFormState({
                          ...formState,
                          selectedProducts: [...formState.selectedProducts, p]
                        });
                      }
                    }}
                    style={{
                      padding: '10px 16px',
                      gap: 16,
                      alignItems: 'center',
                      borderBottom: 'solid 1px #dee2e6',
                      cursor: 'pointer'
                    }}
                  >
                    <Checkbox checked={selected} onChange={() => {}} />
                    <AppStack
                      style={{
                        width: 40,
                        height: 40,
                        border: 'solid 1px #dee2e6'
                      }}
                    >
                      <Image h={40} src={p.previewImage} w={40} />
                    </AppStack>
                    <AppStack style={{ gap: 0 }}>
                      <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                        {p.name}
                      </AppText>
                      <AppText
                        style={{ fontSize: 12, color: productStatusInfo.color }}
                      >
                        {productStatusInfo.label}
                      </AppText>
                    </AppStack>
                  </AppFlexbox>
                );
              })
          )}
        </AppStack>
      </AppStack>
    </ResponsiveModal>
  );
};

VendorProductAssignModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  selectedProducts: PropTypes.array,
  showCategoryFilter: PropTypes.bool,
  showCollectionFilter: PropTypes.bool,
  showDesignFilter: PropTypes.bool,
  showProductStatusFilter: PropTypes.bool,
  title: PropTypes.string
};

export default VendorProductAssignModal;
